import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { saveAs } from 'file-saver';
import { PdfViewerComponent } from '@app/components';
import { ApplicantAttachment } from '@app/models/recruiting/applicant-attachments.model';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { JobAttachment } from '@app/models/recruiting/job-attachments.model';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { FileHelperService, NotifyService, UrlHelperService } from '@app/services';
import { JobAttachmentTypes } from '../../enums/job-attachment-types.enum';
import { ApplicantProfileService } from '../../services';
import { ApplicantDocumentsIndexTable } from '../../services/tables/applicant-documents-table/applicant-documents-index.table';

@Component({
    selector: 'app-applicant-documents',
    templateUrl: './applicant-documents.template.html',
    styleUrls: ['./applicant-documents.style.scss'],
})
export class ApplicantDocumentsComponent implements OnInit {
    @ViewChild('pdfViewer', { static: true }) pdfViewer: PdfViewerComponent;
    @Input() applicant: Applicant;
    applicantDocumentsIndexTable: ApplicantDocumentsIndexTable;
    isLoading = false;
    isDialogLoading = false;
    isDeleteAttachmentDialogOpen = false;
    isRenameAttachmentDialogOpen = false;
    isAddAdditionalDocumentModalOpen = false;
    isDocumentPreviewDialogOpen = false;
    pdfError = false;
    applicantAttachmentSelected: ApplicantAttachment | null = null;
    applicantAttachments: ApplicantAttachment[] = [];
    defaultJobPostingAttachmentsNotUploaded: ApplicantAttachment[] = [];
    jobAttachments: JobAttachment[] = [];
    noOfApplicantDocumentsWithoutJobAttachment = 0;
    allowedFileTypes = ['.pdf', '.csv', '.docx', '.xlsx'];

    applicantDocument: File | null = null;
    applicantDocumentDisplayName = '';

    constructor(
        private urlHelper: UrlHelperService,
        private fileHelper: FileHelperService,
        private notify: NotifyService,
        private applicantProfileService: ApplicantProfileService
    ) {}

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        try {
            this.applicantDocumentsIndexTable = new ApplicantDocumentsIndexTable(this.applicant);
            await this.applicantDocumentsIndexTable.reload();
            this.jobAttachments = await this.applicantProfileService.getJobAttachments(this.applicant.jobPostingId);
            this.processJobAttachments();
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;

        this.pdfViewer.statusChange.subscribe((event: { status: any; error: any }) => {
            this.pdfError = event.error !== null;
        });
    }

    menuAction(event: MenuColumnEvent): void {
        const { menuItem, data } = event;
        const applicantAttachment = data as ApplicantAttachment;
        switch (menuItem.event) {
            case ApplicantDocumentsIndexTable.deleteOption.event:
                this.applicantAttachmentSelected = applicantAttachment;
                this.isDeleteAttachmentDialogOpen = true;
                break;
            case ApplicantDocumentsIndexTable.editOption.event:
                this.applicantAttachmentSelected = applicantAttachment;
                this.applicantDocumentDisplayName = applicantAttachment.name;
                this.isRenameAttachmentDialogOpen = true;
                break;
            case ApplicantDocumentsIndexTable.downloadOption.event:
                this.downloadFile(applicantAttachment);
                break;
        }
    }

    async onApplicantDocumentClicked(applicantAttachment: ApplicantAttachment): Promise<void> {
        this.applicantAttachmentSelected = applicantAttachment;
        this.isDocumentPreviewDialogOpen = true;

        this.isDialogLoading = true;
        await this.pdfViewer.setSource(
            this.fileHelper.path(
                applicantAttachment.convertedFileId ? applicantAttachment.convertedFileId : applicantAttachment.fileId
            )
        );

        this.isDialogLoading = false;
    }

    async onDelete(): Promise<void> {
        this.closeDeleteAttachmentDialog();

        this.isLoading = true;

        try {
            await this.applicantAttachmentSelected.delete();

            await this.applicantDocumentsIndexTable.reload();
            this.processJobAttachments();
        } catch (err) {
            this.notify.error(err.message || 'applicant-tracker.couldNotDeleteApplicantAttachment');
        }

        this.isLoading = false;
    }

    async saveApplicantDocument(): Promise<void> {
        if (!this.applicantDocument || !this.applicantDocumentDisplayName) {
            return;
        }

        this.closeAddAdditionalDocumentModal();

        this.isLoading = true;

        try {
            const savedDocument = await this.fileHelper.store(this.applicantDocument);

            const newApplicantAttachment = new ApplicantAttachment({
                applicantId: this.applicant.id,
                jobAttachmentId: null,
                fileId: savedDocument.id,
                name: this.applicantDocumentDisplayName,
            });

            await newApplicantAttachment.save();

            // Refresh our list of applicant attachment
            await this.applicantDocumentsIndexTable.reload();

            // Clear the selected document in the ui dialog
            this.resetSelectedDocument();

            this.notify.success({
                key: 'applicant-tracker.documentWasSuccessfullyAddedToProfile',
                params: { document: this.applicantDocumentDisplayName },
            });
        } catch (err) {
            this.notify.error(err.message || 'applicant-tracker.couldNotSaveDocument');
        }

        this.isLoading = false;
    }

    async updateApplicantDocument(): Promise<void> {
        if (!this.applicantDocumentDisplayName) {
            return;
        }

        this.closeRenameAttachmentDialog();

        this.isLoading = true;

        try {
            const previousApplicantAttachmentName = this.applicantAttachmentSelected.name;
            this.applicantAttachmentSelected.name = this.applicantDocumentDisplayName;

            await this.applicantAttachmentSelected.save();

            // Refresh our list of applicant attachment
            await this.applicantDocumentsIndexTable.reload();

            this.notify.success({
                key: 'applicant-tracker.nameWasSuccessfullyUpdatedToNewName',
                params: { name: previousApplicantAttachmentName ?? '', newName: this.applicantDocumentDisplayName },
            });

            // Clear the selected document in the ui dialog
            this.resetSelectedDocument();
        } catch (err) {
            this.notify.error(err.message || 'applicant-tracker.couldNotUpdateDocument');
        }

        this.isLoading = false;
    }

    async onDefaultJobPostingAttachmentFileUpload(file: File, applicantAttachment: ApplicantAttachment): Promise<void> {
        if (!file) {
            return;
        }

        this.isLoading = true;

        try {
            const savedFile = await this.fileHelper.store(file);
            applicantAttachment.fileId = savedFile.id;
            await applicantAttachment.save();

            await this.applicantDocumentsIndexTable.reload();
            this.processJobAttachments();

            this.notify.success('applicant-tracker.fileUploaded');
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    downloadFile(applicantAttachment: ApplicantAttachment): void {
        const path = this.fileHelper.path(applicantAttachment.fileId);

        this.urlHelper.getBlob(path).subscribe((data) => {
            saveAs(data, applicantAttachment.file.name);
        });
    }

    closeDeleteAttachmentDialog(): void {
        this.isDeleteAttachmentDialogOpen = false;
    }

    closeRenameAttachmentDialog(): void {
        this.isRenameAttachmentDialogOpen = false;
    }

    showAddAdditionalDocumentModal(): void {
        this.isAddAdditionalDocumentModalOpen = true;
    }

    closeAddAdditionalDocumentModal(): void {
        this.isAddAdditionalDocumentModalOpen = false;
    }

    closeDocumentPreviewDialog(): void {
        this.isDocumentPreviewDialogOpen = false;
    }

    resetSelectedDocument(): void {
        this.applicantDocument = null;
        this.applicantDocumentDisplayName = '';
    }

    onDocumentSelected(files: FileList): void {
        this.applicantDocument = files[0];

        this.applicantDocumentDisplayName =
            this.applicantDocument.name?.substring(0, this.applicantDocument.name.lastIndexOf('.')) || '';
    }

    // TODO: Ticket created for functions below potential for future refactor alongside usage in application tab
    // Process and add any job attachments that are not in the applicant attachment array.
    private processJobAttachments(): void {
        // Filter the jobAttachments based on if a corresponding applicant attachment is found.
        // This means theres a value that exists for the applicant and we should use the applicant attachment.
        const filteredJobAttachments = this.filterByJobAttachments();

        if (filteredJobAttachments.length) {
            this.defaultJobPostingAttachmentsNotUploaded =
                this.convertJobAttachmentsToApplicantAttachments(filteredJobAttachments);
            this.defaultJobPostingAttachmentsNotUploaded = this.defaultJobPostingAttachmentsNotUploaded.filter(
                (applicantAttachment: ApplicantAttachment) => {
                    return (
                        applicantAttachment.jobAttachment?.jobAttachmentTypeId === JobAttachmentTypes.File ||
                        !!applicantAttachment['fileId']
                    );
                }
            );
        }
    }

    private filterByJobAttachments(): JobAttachment[] {
        this.applicantAttachments = this.applicantDocumentsIndexTable?.paginator.values;

        return this.jobAttachments.filter((jobAttachment: JobAttachment) => {
            return !this.applicantAttachments.find((applicantAttachment: ApplicantAttachment) => {
                return applicantAttachment.jobAttachmentId === jobAttachment.id;
            });
        });
    }

    // We need to convert JobAttachments as they are only a indication of what files a user can upload but to actually save files we must
    // save them as Applicant Attachments
    private convertJobAttachmentsToApplicantAttachments(jobAttachments: JobAttachment[]): ApplicantAttachment[] {
        return jobAttachments.map((jobAttachment: JobAttachment) => {
            return this.createApplicantAttachmentFromJobAttachment(jobAttachment);
        });
    }

    private createApplicantAttachmentFromJobAttachment(jobAttachment: JobAttachment): ApplicantAttachment {
        const newApplicantAttachment = new ApplicantAttachment({
            applicantId: this.applicant.id,
            jobAttachmentId: jobAttachment?.id,
            fileId: jobAttachment.fileId ? jobAttachment.fileId : undefined,
            content: jobAttachment.link ? jobAttachment.link : undefined,
        });

        newApplicantAttachment.jobAttachment = jobAttachment;

        return newApplicantAttachment;
    }
}
